import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@fly/core';
import { getFullArtifactVersion, toastService } from '@fly/shared';
import { closeDialog, openDialog, useAppDispatch } from '@fly/store';

import { useDeleteArtifactVersionMutation } from '../store/registry.service';

function DeleteArtifactConfirmationDialog({
  version,
  packageType,
  name,
}: {
  version: string;
  packageType: string;
  name: string;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [deleteArtifactVersion, { isLoading: isDeletingArtifactVersion }] =
    useDeleteArtifactVersionMutation();

  return (
    <div className='mt-fly-4 flex flex-row justify-end gap-fly-3'>
      <Button
        size='2'
        variant='soft'
        color='neutral'
        onClick={() => dispatch(closeDialog())}
      >
        {t('app.dashboard.artifacts.buttons.cancel', {
          defaultValue: 'Cancel',
        })}
      </Button>
      <Button
        size='2'
        variant='soft'
        color='error'
        onClick={() =>
          deleteArtifactVersion({
            packageName: name,
            packageVersion: version,
            packageType,
          }).then((response) => {
            dispatch(closeDialog());

            if (response.error) {
              return;
            }
            toastService.success(
              t('app.dashboard.artifacts.deleted_successfully', {
                defaultValue: 'Artifact deleted successfully',
              })
            );
          })
        }
        loading={isDeletingArtifactVersion}
      >
        {t('app.dashboard.artifacts.buttons.delete', {
          defaultValue: 'Delete',
        })}
      </Button>
    </div>
  );
}

export function useDeleteArtifactConfirmation() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  return useCallback(
    ({
      name,
      version,
      packageType,
    }: {
      name: string;
      version: string;
      packageType: string;
    }) => {
      dispatch(
        openDialog({
          width: '2',
          title: t(
            'app.dashboard.artifacts.delete_artifact_confirmation_title',
            {
              defaultValue: 'Are you sure you want to delete {{artifactName}}?',
              artifactName: getFullArtifactVersion(name, version, packageType),
            }
          ),
          description: t(
            'app.dashboard.artifacts.delete_artifact_confirmation_description',
            {
              defaultValue:
                'This action is irreversible and permanently remove it and all related data.',
            }
          ),
          content: (
            <DeleteArtifactConfirmationDialog
              version={version}
              packageType={packageType}
              name={name}
            />
          ),
        })
      );
    },
    [dispatch, t]
  );
}
